"use client";

import styles from "./HeroSubBanner.module.scss";
import Image from "next/image";
import StatsContainer from "../StatsContainer/StatsContainer";
import cn from "clsx";
import { useEffect, useRef } from "react";
import { pushToDataLayer } from "@/helpers/gtag";
import { useModalDispatch } from "@/context/ModalContext";
import { IModalActions, ModalTypes } from "@/types/modal";
import { usePathname } from "next/navigation";
import { getGlobalStateValue, updateStateSession } from "@/context/utils";
import useOnScreen from "@/helpers/Onscreen";
import { scheduleAfterPaint } from "@/helpers/scheduleAfterPaint";

const IMG_BASE_URL = process.env.NEXT_PUBLIC_NEW_IMAGE_PATH;
interface SubBannerContentProps {
  associatedPrograms: any;
  Brochure?: {
    data?: {
      attributes?: {
        url?: string;
      };
    };
  };
  isOneYearMba: boolean;
  BannerImageTemplete: any;
}

const AffiliatedLogoContent = ({
  headImageOne = "",
  headImageOneTitle = "",
  headImageTwo = "",
  headImageTwoTitle = "",
}) => {
  const pathname = usePathname();

  const isMBAPage =
    pathname == "/master-of-business-administration-online-v1" ||
    pathname == "/master-of-business-administration-online" ||
    pathname == "/bba-mba" ||
    pathname == "/bba-mba-v1" ||
    pathname == "/bcom-mba" ||
    pathname == "/bcom-mba-v1";

  const isOneYearMba =
    pathname === "/one-year-mba-v1" ||
    pathname === "/one-year-mba";

  const isRajastanPage =
    pathname == "/msc-in-data-science" || pathname == "/msc-in-data-science-v1";

  return (
    <div className={styles.affiliated_logo_content}>
      <div className={styles.ugc_ent}>
        <Image
          src={headImageOne || IMG_BASE_URL + "_s/gyan_vigyan_a23375b79d.svg"}
          width={22}
          height={22}
          alt="gyan vigyan logo"
          fetchPriority="low"
        />
        <span className="text-[#1A325D]">
          {headImageOneTitle || "UGC Entitled"}
        </span>
      </div>

      {headImageTwo && (
        <div className={styles.ugc_ent}>
          <Image
            src={headImageTwo}
            width={22}
            height={22}
            alt="head image two logo"
            fetchPriority="low"
          />
          <span className="text-[#1A325D]">
            {headImageTwoTitle || "Top 10 In Asia Pacific"}
          </span>
        </div>
      )}

      {(isMBAPage || isOneYearMba) && <div className={styles.asia_pacific}>
        <div className={styles.img_container}>
          <Image
            src={IMG_BASE_URL + "_s/QS_Icon_2c33592d23.svg"}
            width={22}
            height={22}
            alt="qs icon logo"
            className="h-full flex-1"
          />
        </div>
        <span className="text-[#1A325D] capitalize">top 10 in asia pacific</span>
        <span className={styles.tooltip}>QS Ranked Online MBA, 2024</span>
      </div>
      }

      {isRajastanPage && (
        <div className={styles.ugc_ent}>
          <span className="text-[#1A325D] capitalize">
            Offered by Amity University Rajasthan
          </span>
        </div>
      )}
    </div>
  );
};

const CourseName = ({ ProgramHeading }) => {
  return (
    <div
      className={styles.course_name}
      dangerouslySetInnerHTML={{ __html: ProgramHeading }}
    />
  );
};

const Duration = ({ duration, isOneYearMba = false }) => {
  return (
    <div className={cn(styles.duration, { ["!my-3"]: !isOneYearMba })}>
      <Image
        src={IMG_BASE_URL + "_s/duration_watch_4c87a93418.svg"}
        width={22}
        height={22}
        alt="duration logo"
      />
      <span className="lining-nums">{duration}</span>
    </div>
  );
};

const BrouchreDownloadContainer = ({
  associatedPrograms,
  Brochure = {},
  isOneYearMba = false,
  BannerImageTemplete
}: SubBannerContentProps) => {
  const modalDispatch = useModalDispatch();
  const BrochureLink = Brochure?.data?.attributes?.url;

  const ref = useRef(null);
  const onScreen = useOnScreen(ref);

  useEffect(() => {
    const callIcon = document.getElementById("call-icon");
    const whatsapp = document.getElementById("whatts-app-journey");
    const chatButtonIcon = document.getElementById("chat-launcher-btn");
    const enrollNowRef = document.getElementById("enroll-now-cta");
   
    if (callIcon) {
      if (onScreen) {
        callIcon.style.display = "none";
      } else {
        callIcon.style.display = "block";
      }
    }

    if (whatsapp) {
      if (onScreen) {
        whatsapp.style.display = "none";
      } else {
        whatsapp.style.display = "block";
      }
    }

    if (chatButtonIcon) {
      if (onScreen) {
        chatButtonIcon.style.display = "none";
      } else {
        chatButtonIcon.style.display = "block";
      }
    }

    if (enrollNowRef) {
      if (onScreen) {
        enrollNowRef.style.display = "none";
      } else {
        enrollNowRef.style.display = "block";
      }
    }
  
  }, [onScreen]);

  const downloadBrochurePopup = () => {
    modalDispatch({
      type: IModalActions.OpenModal,
      data: {
        modalType: ModalTypes.ConnectModalMba,
        modalData: {
          rfiCase: "DOWNLOAD_BROCHURE_CASE",
          attachmentURL: BrochureLink,
          associatedPrograms,
          modalVariant: "downloadBrochure",
        },
      },
    });

    setTimeout(() => {
      updateStateSession({
        leadStartingCta: "Download Brochure",
        parentSection: "hero_banner",
      });
      pushToDataLayer({
        event: "lead_initiate",
        cta_text: "Download Brochure",
        section_name: "hero_banner",
        page_type: getGlobalStateValue("pageType"),
        page_url_custom: location.href,
      });

      window?.webengage?.track("Download Brochure Clicked", {
        "Page Type": getGlobalStateValue("pageType"),
        "Section Name": "Hero Banner",
      });
    }, 2000);
  };

  const handleBtnClicked = () => {

    modalDispatch({
      type: IModalActions.OpenModal,
      data: {
        modalType: ModalTypes.ConnectModalMobileNumber,
        modalData: {
          heading: "Join India's No. 1 Online University",
          submitBtnText: "Submit",
          isLandingPage: true,
          associatedPrograms,
          rfiCase: "REDIRECT_APPLICATION_JOURNEY"
        },
      },
    });

    // Same events/payloads — after modal open paints (keeps open-click INP low).
    scheduleAfterPaint(() => {
      pushToDataLayer({
        event: "lead_initiate",
        cta_text: "Get Free Counselling",
        section_name: "Get Free Counselling Program Page",
        page_type: getGlobalStateValue("pageType"),
        page_url: window.location.href,
      });

      updateStateSession({
        parentSection: "Get Free Counselling Program Page",
        leadStartingCta: "Get Free Counselling Program Page",
      });

      window?.webengage?.track("Get Free Counselling Program Page Cliked", {
        "Page Type": getGlobalStateValue("pageType"),
      });
    });
  };



  const handleShare = () => {
    modalDispatch({
      type: IModalActions.OpenModal,
      data: {
        modalType: ModalTypes.ShareIcon,
        // modalData: { programTitle: BannerData?.Heading?.text },
      },
    });
  };

  return (
    <div ref={ref} className={cn(styles.brochure_share_btn_container, { ["my-3"]: isOneYearMba })}>
      <button className={cn(styles.ctaPrimary, styles.hidden)} onClick={handleBtnClicked} id="btn_program_get_free_couselling">
        Get Free Counselling
      </button>
      <button onClick={downloadBrochurePopup} className={styles.download_br}>
        download brochure
        <Image
          src={IMG_BASE_URL + "_s/down_arrow_db_2b837a8c53.svg"}
          width={23}
          height={23}
          alt="download brochure logo"
        />
      </button>
      <button onClick={handleShare} className={styles.share_icon}>
        <Image
          src={IMG_BASE_URL + "_s/share_icon_5defc5b1ac.svg"}
          width={23}
          height={23}
          alt="share icon logo"
        />
      </button>
      {
        isOneYearMba && (
          <Duration duration={BannerImageTemplete?.Duration} isOneYearMba={isOneYearMba} />
        )
      }
    </div>
  );
};

const GlobalCred = ({ BannerImageTemplete }) => {
  return (
    <div className={styles.global_accred}>
      <span className={styles.global_text}>
        {BannerImageTemplete.GlobalAccredationText}
      </span>
      {BannerImageTemplete.GlobalAccredationImageOne && (
        <Image
          src={
            BannerImageTemplete.GlobalAccredationImageOne.data.attributes.url
          }
          width={120}
          height={25}
          alt="WASC"
        />
      )}
      {BannerImageTemplete.GlobalAccredationImageTwo && (
        <Image
          src={
            BannerImageTemplete.GlobalAccredationImageTwo.data.attributes.url
          }
          width={60}
          height={25}
          alt="Times Higher"
        />
      )}
      {BannerImageTemplete.GlobalAccredationImageThree?.data && (
        <Image
          src={
            BannerImageTemplete.GlobalAccredationImageThree.data.attributes.url
          }
          width={75}
          height={25}
          alt="QAA"
        />
      )}
      {BannerImageTemplete.GlobalAccredationImageFour?.data && (
        <Image
          src={
            BannerImageTemplete.GlobalAccredationImageFour.data.attributes.url
          }
          width={25}
          height={25}
          alt="WES"
        />
      )}
    </div>
  );
};

const SubBannerContent = ({
  isMobile = false,
  BannerImageTemplete,
  associatedPrograms,
  Brochure = {},
  isOneYearMba = false
}) => {
  const headImageOne = BannerImageTemplete?.HeadImageOne?.data?.attributes?.url;
  const headImageOneTitle = BannerImageTemplete?.HeadImageOneTitle;
  const headImageTwo = BannerImageTemplete?.HeadImageTwo?.data?.attributes?.url;
  const headImageTwoTitle = BannerImageTemplete?.HeadImageTwoTitle;

  return (
    <div className={styles.content_container}>
      <AffiliatedLogoContent
        headImageOne={headImageOne}
        headImageOneTitle={headImageOneTitle}
        headImageTwo={headImageTwo}
        headImageTwoTitle={headImageTwoTitle}
      />
      <CourseName ProgramHeading={BannerImageTemplete?.ProgramHeading} />
      {
        isOneYearMba && (
          <span className={styles.el_line}>Eligibility: 4-year undergraduate programs (FYUP), as per NEP 2020</span>
        )
      }
      {
        !isOneYearMba && (
          <Duration
            duration={BannerImageTemplete?.Duration}
            isOneYearMba={isOneYearMba}
          />
        )
      }
      <BrouchreDownloadContainer
        Brochure={Brochure}
        associatedPrograms={associatedPrograms}
        isOneYearMba={isOneYearMba}
        BannerImageTemplete={BannerImageTemplete}
      />
      {
        BannerImageTemplete?.ShortDescLabel1 && (
          <StatsContainer BannerImageTemplete={BannerImageTemplete} isStatsDisplay={!isMobile} isOneYearMba={isOneYearMba} />
        )
      }
      {
        BannerImageTemplete?.GlobalAccredationText && (
          <GlobalCred BannerImageTemplete={BannerImageTemplete} />
        )
      }
    </div >
  );
};

export default SubBannerContent;
